/**
 * Storage Service Module
 * Quản lý localStorage operations
 */

import * as StateManager from './state-manager.js';

/**
 * Save courses to localStorage (Debounced for performance)
 */
export function saveCoursesToStorage() {
    const timeout = StateManager.getSaveStorageTimeout();
    clearTimeout(timeout);

    const newTimeout = setTimeout(function() {
        try {
            const dataToSave = {
                courses: StateManager.getSelectedCourses(),
                timestamp: Date.now()
            };
            localStorage.setItem('ktvhvn_selected_courses', JSON.stringify(dataToSave));
        } catch (e) {
            console.error('Cannot save to localStorage:', e);
        }
    }, 500);

    StateManager.setSaveStorageTimeout(newTimeout);
}

/**
 * Load courses from localStorage
 */
export function loadCoursesFromStorage() {
    try {
        const stored = localStorage.getItem('ktvhvn_selected_courses');
        if (!stored) {
            return;
        }

        const data = JSON.parse(stored);

        // Check if data is not too old (24 hours)
        const maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
        if (Date.now() - data.timestamp > maxAge) {
            localStorage.removeItem('ktvhvn_selected_courses');
            return;
        }

        const coursesData = StateManager.getCoursesData();
        const selectedCourses = StateManager.getSelectedCourses();

        // Restore courses if they exist in current course list
        if (data.courses && Array.isArray(data.courses)) {
            data.courses.forEach(savedCourse => {
                // Verify course still exists in current data
                const courseExists = coursesData.find(c => c.id === savedCourse.product_id);
                if (courseExists) {
                    selectedCourses.push(savedCourse);
                }
            });
        }
    } catch (e) {
        console.error('Cannot load from localStorage:', e);
        localStorage.removeItem('ktvhvn_selected_courses');
    }
}

/**
 * Clear courses from localStorage
 */
export function clearCoursesFromStorage() {
    try {
        localStorage.removeItem('ktvhvn_selected_courses');
    } catch (e) {
        console.error('Cannot clear localStorage:', e);
    }
}

/**
 * Save customer info to localStorage
 */
export function saveCustomerInfo() {
    try {
        const $cache = StateManager.getCache();
        // Check if cache elements are initialized before accessing
        const customerData = {
            name: $cache.name ? $cache.name.val().trim() : '',
            phone: $cache.phone ? $cache.phone.val().trim() : '',
            email: $cache.email ? $cache.email.val().trim() : '',
            note: $cache.note ? $cache.note.val().trim() : ''
        };
        localStorage.setItem('ktvhvn_customer_info', JSON.stringify(customerData));
    } catch (e) {
        console.error('Cannot save customer info:', e);
    }
}

/**
 * Load customer info from localStorage
 */
export function loadCustomerInfo() {
    try {
        const stored = localStorage.getItem('ktvhvn_customer_info');
        if (stored) {
            const data = JSON.parse(stored);
            const $cache = StateManager.getCache();
            // Check if cache elements are initialized before accessing
            if ($cache.name) $cache.name.val(data.name || '');
            if ($cache.phone) $cache.phone.val(data.phone || '');
            if ($cache.email) $cache.email.val(data.email || '');
            if ($cache.note) $cache.note.val(data.note || '');
        }
    } catch (e) {
        console.error('Cannot load customer info:', e);
    }
}

/**
 * Generate hash of current form data
 */
export function generateFormDataHash() {
    const $cache = StateManager.getCache();
    const selectedCourses = StateManager.getSelectedCourses();
    const selectedCampaignId = StateManager.getSelectedCampaignId();

    // Collect all relevant form data (with null checks)
    const formData = {
        name: $cache.name ? $cache.name.val().trim() : '',
        phone: $cache.phone ? $cache.phone.val().trim() : '',
        email: $cache.email ? $cache.email.val().trim() : '',
        note: $cache.note ? $cache.note.val().trim() : '',
        courses: selectedCourses.map(course => ({
            product_id: course.product_id,
            variation_id: course.selected_variation ? course.selected_variation.variation_id : 0,
        })),
        campaign_id: selectedCampaignId || '',
    };

    // Convert to JSON string
    const dataString = JSON.stringify(formData);

    // Generate simple hash (using built-in JS string hash)
    let hash = 0;
    for (let i = 0; i < dataString.length; i++) {
        const char = dataString.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
    }

    return hash.toString();
}

/**
 * Clear submitted form hash
 */
export function clearSubmittedHash() {
    localStorage.removeItem('ktvhvn_last_submitted_hash');
}

/**
 * Get traffic tracking data from localStorage
 */
export function getTrafficTrackingData() {
    try {
        const stored = localStorage.getItem('ktvhvn_traffic_source');
        if (stored) {
            const data = JSON.parse(stored);

            // Check if data is not too old (30 days)
            const maxAge = 30 * 24 * 60 * 60 * 1000;
            if (data.first_touch && (Date.now() - data.first_touch.timestamp > maxAge)) {
                // Data too old, reset
                return { first_touch: null, last_touch: null };
            }

            return data;
        }
    } catch (e) {
        console.error('Cannot load traffic tracking:', e);
    }

    return { first_touch: null, last_touch: null };
}

/**
 * Save traffic tracking data to localStorage
 */
export function saveTrafficTrackingData(data) {
    try {
        localStorage.setItem('ktvhvn_traffic_source', JSON.stringify(data));
    } catch (e) {
        console.error('Cannot save traffic tracking:', e);
    }
}
